import ProjectIcon from "@mui/icons-material/Assignment";
import OrganizationIcon from "@mui/icons-material/Business";
import ProductIcon from "@mui/icons-material/Inventory2";
import KindIcon from "@mui/icons-material/Widgets";
import { Box, Tooltip, useTheme } from "@mui/material";
import { Theme, alpha } from "@mui/material/styles";
import {
  BaseEdge,
  Edge,
  EdgeLabelRenderer,
  EdgeProps,
  Handle,
  Node,
  NodeProps,
  Position,
} from "@xyflow/react";
import React from "react";

import { ChartPalette, labelBacking, monospaceStyle } from "../../../theme";
import {
  Ecosystem,
  EcosystemEdge,
  EcosystemEdgeType,
  EcosystemLaneCount,
  EcosystemNode,
  EcosystemNodeType,
} from "../../../types/analytics";
import { EntityRef } from "../../../types/productPlane";
import { GLYPH_SIZES, LABEL_WIDTH } from "./ecosystemLayout";

// The opacity of a node or a line the selection does not keep lit
const DIMMED_OPACITY = 0.25;

// The ring guides: solid and a little stronger than a hairline, as the
// missions view draws its rings, but in no colour: a ring is a distance
export const ringGuideOf = (chart: ChartPalette): string => alpha(chart.ink, 0.16);
const RING_WIDTH = 1.5;

// The colours of the missions view, so that the two read as one: an
// organization in its violet, a product (and a kind) in its pink, a
// programme in no colour and the centre in the accent. Each glyph is a tint
// over the paper with a muted border, so that the hue stays sparing.
export const typeColoursOf = (chart: ChartPalette): Record<EcosystemNodeType, string> => ({
  product: chart.product,
  organization: chart.organization,
  project: chart.programme,
  kind: chart.product,
});
// The icon in a glyph, lighter than its border, as the missions' text
const typeTextOf = (chart: ChartPalette): Record<EcosystemNodeType, string> => ({
  product: chart.productText,
  organization: chart.organizationText,
  project: chart.programmeText,
  kind: chart.productText,
});
// How strongly a glyph is drawn: the opacity of its border and of its tint.
// A node outside the selection is drawn at half.
const GLYPH_LOOK = { border: 0.75, tint: 0.16 } as const;
const OUTSIDE_LOOK = { border: 0.4, tint: 0.08 } as const;
const CENTRE_TINT = 0.3;

// The icon of each type of node, as the detail views head it
const TYPE_ICONS: Record<EcosystemNodeType, typeof ProductIcon> = {
  product: ProductIcon,
  organization: OrganizationIcon,
  project: ProjectIcon,
  kind: KindIcon,
};

// The lines are grey, as the missions view's spokes, and each is stated by
// a document. A part or a payload and its host are joined lighter and
// heavier, so that what a product is made of reads strongest; a variant is
// dashed, so that it is not read as a part; another stated link (a system
// used, an interface) is thin; a role or programme link darker. The lines
// of the node in focus take the accent.
export const VARIANT_DASH = "4 4";
// Articles from which a part or payload is drawn at its full weight
const HEAVY_ARTICLES = 3;
// How much of a line shows: at rest, lit by the focus, on the focus's way
// to the centre, and out of it
export const LINE_OPACITY = { rest: 1, active: 1, route: 0.55, dimmed: 0.15 } as const;

export const counted = (count: number, noun: string): string =>
  `${count} ${noun}${count === 1 ? "" : "s"}`;

// How a type of node reads to the user; a programme is a `project`
export const NODE_TYPE_LABELS: Record<EcosystemNodeType, string> = {
  product: "product",
  organization: "organization",
  project: "programme",
  kind: "kind",
};

export const nodeName = (node: EcosystemNode): string =>
  node.entity.name ?? node.entity.id;

const refName = (ref: EntityRef): string => ref.name ?? ref.id;

// The kinds the centre falls under, written beneath its name: none for a
// kind, which is its own; two at the most, with how many more
export const centreKindsText = (eco: Ecosystem): string | null => {
  if (eco.centre_type === "kind" || eco.centre_kinds.length === 0) {
    return null;
  }
  const names = eco.centre_kinds.map(refName);
  return names.length > 2
    ? `${names.slice(0, 2).join(", ")} +${names.length - 2}`
    : names.join(", ");
};

export interface RingData extends Record<string, unknown> {
  ring: number;
  // Half its width and half its height
  rx: number;
  ry: number;
  shown: number;
  omitted: number;
  lanes: EcosystemLaneCount[];
}

export type RingFlowNode = Node<RingData, "ring">;

// A solid guide ellipse, drawn beneath the nodes, with no caption: a
// caption on the ring stood in the way of the lines.
export const RingNode: React.FC<NodeProps<RingFlowNode>> = ({ data }) => (
  <Box
    role="group"
    aria-label={`Ring ${data.ring}`}
    data-testid="ring-guide"
    data-ring={data.ring}
    sx={{
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      border: (theme) => `${RING_WIDTH}px solid ${ringGuideOf(theme.palette.chart)}`,
      boxSizing: "border-box",
      pointerEvents: "none",
    }}
  />
);

// How a glyph is toned: the centre in the accent, a node outside the
// selection faint, a node under the floor hatched
export type GlyphTone = "plain" | "centre" | "outside" | "thin";

const GLYPH_SHAPES: Record<EcosystemNodeType, string> = {
  product: "rectangle",
  organization: "circle",
  project: "parallelogram",
  kind: "wide-rectangle",
};

// The lean of a programme's glyph
const SKEW = 12;

interface GlyphProps {
  type: EcosystemNodeType;
  tone: GlyphTone;
  selected?: boolean;
  // Half size, without its icon: the sample of a legend
  sample?: boolean;
}

// The colour a glyph is drawn in: the accent at the centre, else its type's
export const glyphColourOf = (
  type: EcosystemNodeType,
  tone: GlyphTone,
  theme: Theme,
): string =>
  tone === "centre" ? theme.palette.accent.main : typeColoursOf(theme.palette.chart)[type];

// The small box a node is drawn as, tinted in the colour of its type with
// the type's icon in it: a product a rectangle, an organization a circle, a
// programme a leaning box, a kind a wider rectangle. The centre is tinted
// in the accent and haloed, as the missions' centre. The tint is laid over
// the paper, so that no line shows through it.
export const Glyph: React.FC<GlyphProps> = ({
  type,
  tone,
  selected = false,
  sample = false,
}) => {
  const theme = useTheme();
  const paper = theme.palette.background.paper;
  const raised = theme.palette.background.raised;
  const size = GLYPH_SIZES[type];
  const scale = sample ? 0.5 : 1;
  const colour = glyphColourOf(type, tone, theme);
  const Icon = TYPE_ICONS[type];
  const tint = (opacity: number): string =>
    `linear-gradient(${alpha(colour, opacity)}, ${alpha(colour, opacity)})`;
  const tinted = (border: number, fill: number) => ({
    borderWidth: 1.5,
    borderStyle: "solid",
    borderColor: alpha(colour, border),
    backgroundColor: paper,
    backgroundImage: tint(fill),
  });
  const tones: Record<GlyphTone, object> = {
    plain: { ...tinted(GLYPH_LOOK.border, GLYPH_LOOK.tint), color: typeTextOf(theme.palette.chart)[type] },
    outside: {
      ...tinted(OUTSIDE_LOOK.border, OUTSIDE_LOOK.tint),
      color: theme.palette.text.secondary,
    },
    centre: {
      borderWidth: 2,
      borderStyle: "solid",
      borderColor: colour,
      backgroundColor: paper,
      backgroundImage: tint(CENTRE_TINT),
      color: theme.palette.text.primary,
      boxShadow: sample ? undefined : `0 0 0 4px ${alpha(colour, 0.3)}`,
    },
    thin: {
      borderWidth: 1.5,
      borderStyle: "solid",
      borderColor: alpha(colour, 0.35),
      backgroundColor: paper,
      backgroundImage: `repeating-linear-gradient(45deg, ${paper}, ${paper} 3px, ${raised} 3px, ${raised} 6px)`,
      color: theme.palette.text.disabled,
    },
  };
  return (
    <Box
      component="span"
      aria-hidden={sample || undefined}
      data-testid={sample ? "eco-sample" : "eco-shape"}
      data-shape={GLYPH_SHAPES[type]}
      data-tone={tone}
      data-colour={colour}
      sx={{
        flexShrink: 0,
        boxSizing: "border-box",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: size.width * scale,
        height: size.height * scale,
        borderRadius: type === "organization" ? "50%" : sample ? "4px" : "6px",
        transform: type === "project" ? `skewX(-${SKEW}deg)` : undefined,
        ...tones[tone],
        // The selection is ringed in white, off the glyph: every colour of
        // the graph stands for a type
        ...(selected
          ? { boxShadow: `0 0 0 3px ${paper}, 0 0 0 5px ${theme.palette.text.primary}` }
          : {}),
      }}
    >
      {!sample && (
        <Icon
          aria-hidden
          sx={{
            fontSize: 15,
            transform: type === "project" ? `skewX(${SKEW}deg)` : undefined,
          }}
        />
      )}
    </Box>
  );
};

// What a link between two products states, by its kind and then by its
// relation: a part, a payload (mounted, carried, or of no stated kind), a
// member of a set, a variant, a system used, an interface, or another
// relation, read as its words. A link with no relation predates them, and
// was a part.
export type StructureLink =
  | "part"
  | "mounted"
  | "carried"
  | "payload"
  | "member"
  | "variant"
  | "uses"
  | "interfaces"
  | "other";

const BY_LINK_KIND: Record<string, StructureLink> = {
  component: "part",
  mounted_payload: "mounted",
  carried_payload: "carried",
  member_of: "member",
  uses_system: "uses",
  system_link: "uses",
  interfaces_with: "interfaces",
};

const BY_RELATION: Record<string, StructureLink> = {
  has_component: "part",
  carries_payload: "payload",
  has_member: "member",
  variant_of: "variant",
  uses: "uses",
  uses_system: "uses",
  interfaces_with: "interfaces",
};

export const structureLink = (
  edge: Pick<EcosystemEdge, "relation" | "link_kind">,
): StructureLink => {
  if (edge.relation === "variant_of") {
    return "variant";
  }
  const byKind = edge.link_kind ? BY_LINK_KIND[edge.link_kind] : undefined;
  if (byKind) {
    return byKind;
  }
  if (edge.relation === null) {
    return "part";
  }
  return BY_RELATION[edge.relation] ?? "other";
};

// How a link is drawn: what a product is made of or carries, a variant, or
// another stated link
export type StructureLine = "composition" | "variant" | "other";

export const structureLine = (link: StructureLink): StructureLine =>
  link === "variant"
    ? "variant"
    : link === "uses" || link === "interfaces" || link === "other"
      ? "other"
      : "composition";

type LineEdge = Pick<EcosystemEdge, "type" | "articles" | "relation" | "link_kind">;

// The stroke and width of a line by what it says, at rest: a role,
// programme or kind link darker, a link between two products by its line
export const lineStyle = (
  edge: LineEdge,
  chart: ChartPalette,
): { stroke: string; strokeWidth: number; strokeDasharray?: string } => {
  if (edge.type !== "structure") {
    return { stroke: chart.edge, strokeWidth: 1.5 };
  }
  switch (structureLine(structureLink(edge))) {
    case "composition":
      return {
        stroke: chart.muted,
        strokeWidth: (edge.articles ?? 0) >= HEAVY_ARTICLES ? 3 : 2,
      };
    case "variant":
      return { stroke: chart.muted, strokeWidth: 1.5, strokeDasharray: VARIANT_DASH };
    case "other":
      return { stroke: chart.muted, strokeWidth: 1 };
  }
};

// How strongly an edge is drawn, strongest first: what a product is made
// of or carries, a variant, another stated link, a role, a programme, a kind
const lineRank = (edge: LineEdge): number => {
  switch (edge.type) {
    case "structure":
      return { composition: 0, variant: 1, other: 2 }[structureLine(structureLink(edge))];
    case "role":
      return 3;
    case "programme":
    case "participation":
      return 4;
    case "kind":
      return 5;
  }
};

// The edges a line stands for, the one it is drawn as first: the
// strongest, and of two equally strong the one stated in more articles
export const strongestFirst = <T extends LineEdge>(edges: T[]): T[] =>
  [...edges].sort(
    (a, b) => lineRank(a) - lineRank(b) || (b.articles ?? 0) - (a.articles ?? 0),
  );

const STRUCTURE_WORDS: Record<Exclude<StructureLink, "other">, string> = {
  part: "part",
  mounted: "mounted payload",
  carried: "carried payload",
  payload: "payload",
  member: "member",
  variant: "variant",
  uses: "uses",
  interfaces: "interfaces",
};

// What an edge is, in a word or two, as a lit line is labelled: a role by
// its role, a link between products by what it states
export const edgeWord = (edge: EcosystemEdge): string => {
  switch (edge.type) {
    case "structure": {
      const link = structureLink(edge);
      return link === "other" ? relationWords(edge.relation) : STRUCTURE_WORDS[link];
    }
    case "role":
      return edge.role && edge.role !== "other"
        ? edge.role
        : relationWords(edge.relation);
    case "programme":
      return "programme";
    case "participation":
      return relationWords(edge.relation ?? "participant");
    case "kind":
      return "kind";
  }
};

// The label of a line: the words of each edge it stands for, once each
export const lineLabel = (edges: EcosystemEdge[]): string =>
  Array.from(new Set(edges.map(edgeWord))).join(" · ");

// The colour a line is drawn in: the accent while the node in focus is one
// of its ends
export const lineColourOf = (edge: LineEdge, active: boolean, theme: Theme): string =>
  active ? theme.palette.accent.main : lineStyle(edge, theme.palette.chart).stroke;

// A short stretch of line, as the canvas draws it: a structure line by the
// relation given, `lit` in the accent
export const LineSample: React.FC<{
  type: EcosystemEdgeType;
  relation?: string | null;
  lit?: boolean;
}> = ({ type, relation = null, lit = false }) => {
  const theme = useTheme();
  const edge = { type, articles: null, relation, link_kind: null };
  const style = lineStyle(edge, theme.palette.chart);
  const width = Math.min(2, style.strokeWidth);
  return (
    <Box
      component="svg"
      width={26}
      height={8}
      aria-hidden
      sx={{ flexShrink: 0, verticalAlign: "middle" }}
    >
      <line
        x1={1}
        y1={4}
        x2={25}
        y2={4}
        stroke={lineColourOf(edge, lit, theme)}
        strokeWidth={width}
        strokeLinecap="round"
        strokeDasharray={style.strokeDasharray}
      />
    </Box>
  );
};

export interface EcoNodeData extends Record<string, unknown> {
  node: EcosystemNode;
  selected: boolean;
  dimmed: boolean;
  // Under the floor: its count is a hint
  thin: boolean;
  // The kinds the centre falls under; null for every other node
  centreKinds: string | null;
  onSelect: (id: string) => void;
  // The pointer or the keyboard is on the node, or has left it (null)
  onPoint: (id: string | null) => void;
}

// A name longer than this does not fit its two lines, and is given whole
// where the pointer rests on it
const LONG_NAME = 36;

export type EcoFlowNode = Node<EcoNodeData, "eco">;

// What a node says beneath its name: an organization its role and
// country, a programme or a kind what it is, and a node outside the
// selection that it is
export const nodeCaption = (node: EcosystemNode): string => {
  const what =
    node.type === "organization"
      ? node.role
      : node.type === "product"
        ? null
        : NODE_TYPE_LABELS[node.type];
  return [what, node.country, node.outside ? "outside selection" : null]
    .filter(Boolean)
    .join(" · ");
};

export const toneOf = (node: EcosystemNode, thin: boolean): GlyphTone =>
  node.ring === 0 ? "centre" : thin ? "thin" : node.outside ? "outside" : "plain";

// The small line beneath a name: a kind, a caption
const smallLineSx = (theme: Theme) =>
  ({
    px: 0.5,
    borderRadius: "4px",
    backgroundColor: labelBacking(theme),
    color: "text.secondary",
    fontSize: "10.5px",
    lineHeight: 1.3,
  }) as const;

// A node: its glyph, and beneath it its name on two lines at the most and a
// line of what it is; the centre also the kinds it falls under. What stands
// for several products (an organization, a programme, a kind) carries their
// count at the corner of its glyph; a product is one product and carries
// none. The lines meet at the glyph.
export const EcoNode: React.FC<NodeProps<EcoFlowNode>> = ({ data }) => {
  const { node, selected, dimmed, thin, centreKinds, onSelect, onPoint } = data;
  const theme = useTheme();
  const centre = node.ring === 0;
  const caption = nodeCaption(node);
  const name = nodeName(node);
  const tone = toneOf(node, thin);
  const quiet = (thin || node.outside) && !centre;
  const handle: React.CSSProperties = {
    left: "50%",
    top: GLYPH_SIZES[node.type].height / 2,
    opacity: 0,
    pointerEvents: "none",
  };

  return (
    <Box
      role="button"
      tabIndex={0}
      className="nodrag nopan"
      aria-label={nodeName(node)}
      aria-pressed={selected}
      data-type={node.type}
      data-ring={node.ring}
      data-lane={node.lane ?? ""}
      data-centre={centre ? "true" : "false"}
      data-outside={node.outside ? "true" : "false"}
      data-thin={thin ? "true" : "false"}
      data-dimmed={dimmed ? "true" : "false"}
      onKeyDown={(event: React.KeyboardEvent) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect(node.id);
        }
      }}
      onClick={(event) => {
        event.stopPropagation();
        onSelect(node.id);
      }}
      onMouseEnter={() => onPoint(node.id)}
      onMouseLeave={() => onPoint(null)}
      onFocus={() => onPoint(node.id)}
      onBlur={() => onPoint(null)}
      sx={{
        position: "relative",
        width: LABEL_WIDTH,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "3px",
        textAlign: "center",
        cursor: "pointer",
        opacity: dimmed ? DIMMED_OPACITY : 1,
        transition: "opacity 120ms",
        borderRadius: "6px",
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "link.main",
          outlineOffset: 3,
        },
      }}
    >
      <Handle type="target" position={Position.Top} isConnectable={false} style={handle} />
      <Box component="span" sx={{ position: "relative", display: "inline-flex" }}>
        <Glyph type={node.type} tone={tone} selected={selected} />
        {node.type !== "product" && (
          <Box
            component="span"
            aria-label={`${counted(node.product_count, "product")} behind it`}
            data-testid="eco-count"
            sx={{
              ...monospaceStyle,
              position: "absolute",
              top: -7,
              right: -10,
              minWidth: 16,
              height: 15,
              px: "3px",
              boxSizing: "border-box",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "8px",
              border: `1px solid ${alpha(glyphColourOf(node.type, tone, theme), 0.6)}`,
              backgroundColor: "background.paper",
              color: "text.primary",
              fontSize: "9.5px",
              lineHeight: 1,
            }}
          >
            {node.product_count}
          </Box>
        )}
      </Box>
      <Box
        component="span"
        title={name.length > LONG_NAME ? name : undefined}
        sx={{
          maxWidth: "100%",
          px: 0.5,
          borderRadius: "4px",
          backgroundColor: labelBacking(theme),
          color: quiet ? "text.disabled" : "text.primary",
          fontSize: "11.5px",
          fontWeight: quiet ? 400 : 500,
          lineHeight: 1.3,
          overflowWrap: "anywhere",
          // Two lines at the most: the room the layout keeps for a name
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {name}
      </Box>
      {centreKinds && (
        <Box
          component="span"
          data-testid="centre-kinds"
          sx={{ ...smallLineSx(theme), maxWidth: "100%", overflowWrap: "anywhere" }}
        >
          {centreKinds}
        </Box>
      )}
      {caption && (
        <Box component="span" sx={{ ...monospaceStyle, ...smallLineSx(theme) }}>
          {caption}
        </Box>
      )}
      <Handle type="source" position={Position.Bottom} isConnectable={false} style={handle} />
    </Box>
  );
};

export interface EcoEdgeData extends Record<string, unknown> {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  // The edge the line is drawn as, the strongest of those it stands for
  edge: EcosystemEdge;
  // What the line says, a sentence per edge, where the pointer rests on it
  reading: string;
  // The words laid on the line while it is lit, or none
  label: string | null;
  // Whether the node in focus is one of its ends
  active: boolean;
  // Whether it is on the way from the node in focus to the centre
  route: boolean;
  dimmed: boolean;
}

export type EcoFlowEdge = Edge<EcoEdgeData, "eco">;

// Width (px) of the band along a line that answers the pointer
const HOVER_WIDTH = 12;

// A relation as words: `interfaces_with` reads "interfaces with"
const relationWords = (relation: string | null): string =>
  (relation ?? "linked to").replace(/_/g, " ");

// What the node at the other end of a link is to the node in hand ("this"),
// as the panel lists it: where it is the link's object, and where its
// subject
const PARTNER_WORDS: Record<Exclude<StructureLink, "other">, [string, string]> = {
  part: ["part", "host"],
  mounted: ["mounted payload", "carries this"],
  carried: ["carried payload", "carries this"],
  payload: ["payload", "carries this"],
  member: ["member", "has this as a member"],
  variant: ["this is a variant of it", "variant"],
  uses: ["used by this", "uses this"],
  interfaces: ["interfaces with this", "interfaces with this"],
};

export const partnerWords = (edge: EcosystemEdge, partnerIsSource: boolean): string => {
  const link = structureLink(edge);
  if (link === "other") {
    const words = relationWords(edge.relation);
    return partnerIsSource ? `${words} this` : `this ${words} it`;
  }
  return PARTNER_WORDS[link][partnerIsSource ? 1 : 0];
};

// What an edge says in a few words, beside the node at its other end; a
// link between two products says what that node is to this one
export const edgeTitle = (edge: EcosystemEdge, partnerIsSource = false): string => {
  switch (edge.type) {
    case "structure":
      return (
        partnerWords(edge, partnerIsSource) +
        (edge.articles === null ? "" : ` · ${counted(edge.articles, "article")}`)
      );
    case "role":
      return `role · ${edge.role ?? edge.relation ?? "other"}`;
    case "programme":
      return "programme";
    case "participation":
      return `takes part · ${relationWords(edge.relation ?? "participant")}`;
    case "kind":
      return "kind";
  }
};

// What a link between two products says, with the names of its subject
// and its object
export const structureReading = (
  edge: Pick<EcosystemEdge, "relation" | "link_kind">,
  from: string,
  to: string,
): string => {
  switch (structureLink(edge)) {
    case "part":
      return `${to} is a part of ${from}`;
    case "mounted":
      return `${from} carries ${to} as a mounted payload`;
    case "carried":
      return `${from} carries ${to} as a carried payload`;
    case "payload":
      return `${from} carries ${to} as a payload`;
    case "member":
      return `${to} is a member of ${from}`;
    case "variant":
      return `${from} is a variant of ${to}`;
    case "uses":
      return `${from} uses ${to}`;
    case "interfaces":
      return `${from} interfaces with ${to}`;
    case "other":
      return `${from} ${relationWords(edge.relation)} ${to}`;
  }
};

// What an edge says in a sentence, with the names of its two ends. A
// structure edge runs from its subject to its object (the host to the
// part, the variant to the product varied, the user to the system used);
// a role, programme or kind edge from the product (a kind edge only to a
// kind at the centre).
export const edgeReading = (
  edge: EcosystemEdge,
  source: EcosystemNode | undefined,
  target: EcosystemNode | undefined,
): string => {
  const from = source ? nodeName(source) : edge.source;
  const to = target ? nodeName(target) : edge.target;
  switch (edge.type) {
    case "structure":
      return (
        structureReading(edge, from, to) +
        (edge.articles === null ? "" : ` · stated in ${counted(edge.articles, "article")}`)
      );
    case "role":
      return edge.relation
        ? `${from} is ${edge.relation.replace(/_/g, " ")} ${to}`
        : `${to} has a role towards ${from}: ${edge.role ?? "other"}`;
    case "programme":
      return `${from} is linked to the programme ${to}`;
    case "participation":
      return edge.relation
        ? `${from} takes part in ${to} as ${edge.relation.replace(/_/g, " ")}`
        : `${from} takes part in ${to}`;
    case "kind":
      return `${from} is of the kind ${to}`;
  }
};

// A line between two nodes, grey at rest: a part or a payload and its host
// the strongest, a variant dashed, another stated link thin. The lines of
// the node in focus take the accent, its way to the centre a fainter
// accent, the rest stand back.
export const EcoEdgeLine: React.FC<EdgeProps<EcoFlowEdge>> = ({ id, data }) => {
  const theme = useTheme();
  if (!data) {
    return null;
  }
  const path = `M ${data.x1} ${data.y1} L ${data.x2} ${data.y2}`;
  const line = lineStyle(data.edge, theme.palette.chart);
  const stroke = lineColourOf(data.edge, data.active || data.route, theme);
  return (
    <>
      <BaseEdge
        id={id}
        path={path}
        interactionWidth={0}
        style={{
          ...line,
          stroke,
          strokeLinecap: "round",
          opacity: data.dimmed
            ? LINE_OPACITY.dimmed
            : data.active
              ? LINE_OPACITY.active
              : data.route
                ? LINE_OPACITY.route
                : LINE_OPACITY.rest,
          transition: "opacity 120ms",
        }}
      />
      {data.label !== null && (
        <EdgeLabelRenderer>
          <Box
            data-testid="eco-edge-label"
            sx={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${(data.x1 + data.x2) / 2}px, ${(data.y1 + data.y2) / 2}px)`,
              px: 0.5,
              borderRadius: 0.5,
              bgcolor: "background.default",
              border: `1px solid ${alpha(theme.palette.accent.main, 0.5)}`,
              color: theme.palette.accent.main,
              fontSize: 10,
              lineHeight: "14px",
              whiteSpace: "nowrap",
              pointerEvents: "none",
            }}
          >
            {data.label}
          </Box>
        </EdgeLabelRenderer>
      )}
      <Tooltip
        title={data.reading}
        followCursor
        enterDelay={150}
        slotProps={{ tooltip: { sx: { whiteSpace: "pre-line" } } }}
      >
        <path
          d={path}
          fill="none"
          stroke="transparent"
          strokeWidth={HOVER_WIDTH}
          data-testid="eco-edge"
          data-type={data.edge.type}
          data-link={data.edge.type === "structure" ? structureLink(data.edge) : ""}
          data-source={data.edge.source}
          data-target={data.edge.target}
          data-active={data.active ? "true" : "false"}
          data-route={data.route ? "true" : "false"}
          data-stroke={stroke}
          data-width={line.strokeWidth}
          data-dash={line.strokeDasharray ?? ""}
        />
      </Tooltip>
    </>
  );
};
